import type { Coord, GameState, Unit } from "@dailywars/core/engine";
import type { ReactNode } from "react";

import { BoardActionStatusBar } from "#/components/game/BoardActionStatusBar";
import { EndTurnButton } from "#/components/game/EndTurnButton";
import { GameSettingsMenu } from "#/components/game/GameSettingsMenu";
import { TileInspectorBar } from "#/components/game/TileInspectorBar";
import { UndoButton } from "#/components/game/UndoButton";
import { useCanEndTurn, useInspectedUnit } from "#/game/gameSelectors";
import { useGameSelector } from "#/game/GameStoreProvider";
import {
  canUndoCommittedTurnFromState,
  canUndoPendingMovesFromState,
} from "#/game/store/selectors";

import type { PlaySessionConfig } from "../playSession";

function canUndoForSession({
  session,
  boardDialogOpen,
  canUndoPending,
  canUndoCommitted,
}: {
  session: PlaySessionConfig;
  boardDialogOpen: boolean;
  canUndoPending: boolean;
  canUndoCommitted: boolean;
}): boolean {
  if (!session.allowUndo || boardDialogOpen) return false;
  if (canUndoPending) return true;
  return session.allowUndoFullTurn && canUndoCommitted;
}

function inProgressDockActions({
  handleEndTurn,
  canEndTurn,
  handleUndo,
  canUndo,
  isSubmitting,
  handleRestart,
  isRestarting,
  allowUndo,
  allowRestart,
}: {
  handleEndTurn: () => void | Promise<void>;
  canEndTurn: boolean;
  handleUndo: () => void;
  canUndo: boolean;
  isSubmitting: boolean;
  handleRestart: () => void | Promise<void>;
  isRestarting: boolean;
  allowUndo: boolean;
  allowRestart: boolean;
}) {
  return (
    <>
      {allowUndo ? <UndoButton onClick={handleUndo} disabled={!canUndo} /> : null}
      <EndTurnButton onClick={handleEndTurn} disabled={!canEndTurn} pending={isSubmitting} />
      <GameSettingsMenu
        onRestart={handleRestart}
        isRestarting={isRestarting}
        allowRestart={allowRestart}
      />
    </>
  );
}

type PlayBottomDockBarProps = {
  status: GameState["status"];
  localState: GameState;
  inspectedTile: Coord | null;
  inspectedUnit: Unit | null;
  handleEndTurn: () => void | Promise<void>;
  canEndTurn: boolean;
  handleUndo: () => void;
  canUndo: boolean;
  isSubmitting: boolean;
  handleRestart: () => void | Promise<void>;
  isRestarting: boolean;
  allowUndo?: boolean;
  allowRestart?: boolean;
  actions?: ReactNode;
};

export function PlayBottomDockBar({
  status,
  localState,
  inspectedTile,
  inspectedUnit,
  handleEndTurn,
  canEndTurn,
  handleUndo,
  canUndo,
  isSubmitting,
  handleRestart,
  isRestarting,
  allowUndo = true,
  allowRestart = true,
  actions,
}: PlayBottomDockBarProps) {
  const defaultActions =
    status === "in_progress"
      ? inProgressDockActions({
          handleEndTurn,
          canEndTurn,
          handleUndo,
          canUndo,
          isSubmitting,
          handleRestart,
          isRestarting,
          allowUndo,
          allowRestart,
        })
      : undefined;

  return (
    <div className="shrink-0">
      {status === "in_progress" ? <BoardActionStatusBar /> : null}
      <TileInspectorBar
        state={localState}
        tile={inspectedTile}
        unit={inspectedUnit}
        actions={actions ?? defaultActions}
      />
    </div>
  );
}

type PlayMapDockBarsProps = {
  handleUndo: () => void;
  handleEndTurn: () => void | Promise<void>;
  handleRestart: () => void | Promise<void>;
  isRestarting: boolean;
  boardDialogOpen?: boolean;
  session: PlaySessionConfig;
};

export function PlayMapDockBars({
  handleUndo,
  handleEndTurn,
  handleRestart,
  isRestarting,
  boardDialogOpen = false,
  session,
}: PlayMapDockBarsProps) {
  const localState = useGameSelector((s) => s.localState);
  const inspectedTile = useGameSelector((s) => s.hoveredTile);
  const inspectedUnit = useInspectedUnit();
  const isSubmitting = useGameSelector((s) => s.isSubmitting);
  const canEndTurn = useCanEndTurn() && !boardDialogOpen;
  const canUndoPending = useGameSelector(canUndoPendingMovesFromState);
  const canUndoCommitted = useGameSelector(canUndoCommittedTurnFromState);
  const canUndo = canUndoForSession({
    session,
    boardDialogOpen,
    canUndoPending,
    canUndoCommitted,
  });

  return (
    <PlayBottomDockBar
      status={localState.status}
      localState={localState}
      inspectedTile={inspectedTile}
      inspectedUnit={inspectedUnit}
      handleEndTurn={handleEndTurn}
      canEndTurn={canEndTurn}
      handleUndo={handleUndo}
      canUndo={canUndo}
      isSubmitting={isSubmitting}
      handleRestart={handleRestart}
      isRestarting={isRestarting || isSubmitting}
      allowUndo={session.allowUndo}
      allowRestart={session.allowRestart}
    />
  );
}
